import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  findAllTestimonial,
  findAllActiveTestimonial,
  findByIDTestimonial,
  insertTestimonial,
  updateTestimonial,
  activeInactiveTestimonial,
  deleteTestimonial,
} from "./testimonial.service";
import { ITestimonial } from "./testimonialModel";

interface TestimonialState {
  TestimonialList: ITestimonial[];
  ActiveTestimonialList: ITestimonial[];
  TestimonialDetail: ITestimonial | null;
  loading: boolean;
  formLoading: boolean;
  error: string | null;
  isDeleteModalOpen: boolean;
  DeleteTestimonialID: number | null;
}

const initialState: TestimonialState = {
  TestimonialList: [],
  ActiveTestimonialList: [],
  TestimonialDetail: null,
  loading: false,
  formLoading: false,
  error: null,
  isDeleteModalOpen: false,
  DeleteTestimonialID: null,
};

const testimonialSlice = createSlice({
  name: "testimonial",
  initialState,
  reducers: {
    clearTestimonialDetails: (state) => {
      state.TestimonialDetail = null;
    },
    openDeleteModal: (state, action: PayloadAction<number>) => {
      state.isDeleteModalOpen = true;
      state.DeleteTestimonialID = action.payload;
    },
    closeDeleteModal: (state) => {
      state.isDeleteModalOpen = false;
      state.DeleteTestimonialID = null;
    },
  },
  extraReducers: (builder) => {
    // Find All
    builder.addCase(findAllTestimonial.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      findAllTestimonial.fulfilled,
      (state, action: PayloadAction<ITestimonial[]>) => {
        state.loading = false;
        state.TestimonialList = action.payload || [];
      },
    );
    builder.addCase(findAllTestimonial.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch testimonials";
    });

    // Find All Active
    builder.addCase(findAllActiveTestimonial.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      findAllActiveTestimonial.fulfilled,
      (state, action: PayloadAction<ITestimonial[]>) => {
        state.loading = false;
        state.ActiveTestimonialList = action.payload || [];
      },
    );
    builder.addCase(findAllActiveTestimonial.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch active testimonials";
    });

    // Find by ID
    builder.addCase(findByIDTestimonial.pending, (state) => {
      state.formLoading = true;
      state.error = null;
    });
    builder.addCase(
      findByIDTestimonial.fulfilled,
      (state, action: PayloadAction<ITestimonial | null>) => {
        state.formLoading = false;
        state.TestimonialDetail = action.payload;
      },
    );
    builder.addCase(findByIDTestimonial.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Failed to fetch testimonial details";
    });

    // Insert
    builder.addCase(insertTestimonial.pending, (state) => {
      state.formLoading = true;
      state.error = null;
    });
    builder.addCase(insertTestimonial.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(insertTestimonial.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Failed to create testimonial";
    });

    // Update
    builder.addCase(updateTestimonial.pending, (state) => {
      state.formLoading = true;
      state.error = null;
    });
    builder.addCase(updateTestimonial.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(updateTestimonial.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Failed to update testimonial";
    });

    // Active/Inactive
    builder.addCase(activeInactiveTestimonial.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(activeInactiveTestimonial.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(activeInactiveTestimonial.rejected, (state) => {
      state.loading = false;
    });

    // Delete
    builder.addCase(deleteTestimonial.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteTestimonial.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteTestimonial.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const {
  clearTestimonialDetails,
  openDeleteModal,
  closeDeleteModal,
} = testimonialSlice.actions;

export default testimonialSlice.reducer;
